import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { type NavItem, type BadgeState, isPathActive, getNavPath } from '@/lib/navigationItems';
import { useAnalytics } from '@/hooks/useAnalytics';
import { getAnalyticsDeviceContext } from '@/analytics/analyticsConfig';

interface BottomNavButtonProps {
  item: NavItem;
  badgeState?: BadgeState;
  artistId?: string;
  /** Override the label (for hardcoded labels like "Bookings") */
  labelOverride?: string;
}

/**
 * Reusable bottom navigation button component
 * Used in bottom nav bars (UsersBottomNav, ArtistsBottomNav, etc.)
 */
export function BottomNavButton({ 
  item, 
  badgeState = {},
  artistId,
  labelOverride
}: BottomNavButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPressed, setIsPressed] = React.useState(false);
  const { trackEvent } = useAnalytics();

  const path = getNavPath(item, artistId);
  
  // Special handling for artist profile path matching
  const isActive = item.id === 'artistProfile' 
    ? location.pathname === `/artist/${artistId}` || location.pathname === `/${artistId}`
    : item.id === 'artists'
    ? isPathActive(location.pathname, item.pathMatches) || (location.pathname.startsWith('/artist/') && location.pathname !== `/artist/${artistId}`)
    : isPathActive(location.pathname, item.pathMatches);
  
  const badge = item.getBadge?.(badgeState);
  const label = labelOverride || t(item.labelKey);

  const handleClick = () => {
    if (!path || path.includes('/undefined')) {
      console.warn('[BottomNavButton] Blocked navigation to invalid path:', path, { itemId: item.id, artistId });
      return;
    }

    setIsPressed(true);
    const { device, platform } = getAnalyticsDeviceContext();
    trackEvent('nav_click', {
      nav_type: 'bottom',
      item_key: item.id,
      device,
      platform
    });
    navigate(path);
    // Clear pressed state after a short delay
    setTimeout(() => setIsPressed(false), 150);
  };

  return (
    <Button 
      variant="ghost" 
      className="bottom-nav-button !px-0 !py-0 !flex !flex-col !items-center !justify-center gap-1 transition-all duration-150"
      onClick={handleClick}
    >
      <div className={`bottom-nav-icon relative !flex !items-center !justify-center transition-colors duration-150 ${isActive || isPressed ? 'text-primary' : ''} ${item.id === 'artists' ? 'bottom-nav-icon-artists' : ''}`}>
        {item.icon({ style: { width: '100%', height: '100%' } })}
        {/* Badge with count */}
        {badge?.show && badge.count && (
          <div className="absolute -top-2 -right-2 flex items-center justify-center min-w-[0.75rem] h-3 bg-primary rounded-full text-[0.5rem] text-white font-medium px-0.5">
            {badge.count}
          </div>
        )}
        {/* Dot badge (no count) */}
        {badge?.show && badge.dot && !badge.count && (
          <div className="absolute -top-2 -right-2 w-2 h-2 bg-primary rounded-full" />
        )}
      </div>
      <span className="text-bottom-nav">{label}</span>
    </Button>
  );
}
